import { Component, OnInit, ViewChild, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { Router } from '@angular/router';
import { Tenant } from '../../interfaces/Tenant';
import { ClientService } from '../../services/client-service';
import { CommonService } from '../../services/common.service';
import { AoButtonComponent } from '../ao-button/ao-button.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { ClientContextService } from '@ao/app/core/services/client-context.service'; // adjust path if needed
import { UserAccessService } from '../../services/user-access.service';

@Component({
  selector: 'app-client-selection-dialog',
  standalone: true,
  imports: [
    FormsModule,
    AutoCompleteModule,
    ButtonModule,
    AoButtonComponent,
    ConfirmDialogComponent,
  ],
  templateUrl: './client-selection-dialog.component.html',
  styleUrls: ['./client-selection-dialog.component.scss'],
})
export class ClientSelectionDialogComponent implements OnInit {
  allClients: Tenant[] = [];
  filteredClients: Tenant[] = [];
  selectedClient: Tenant | null = null;
  isLoading = false;

  @ViewChild(ConfirmDialogComponent) confirmDialog!: ConfirmDialogComponent;

  private clientService = inject(ClientService);
  private ref = inject(DynamicDialogRef);
  private router = inject(Router);
  private commonService = inject(CommonService);
  private clientContext = inject(ClientContextService);
  private userAccessService = inject(UserAccessService);

  ngOnInit() {
    this.isLoading = true;
    this.clientService.getClients().subscribe({
      next: clients => {
        this.allClients = clients;
        this.isLoading = false;

        // If only one client, auto-select and set context, then close dialog
        if (clients.length === 1) {
          this.selectedClient = clients[0];
          this.clientContext.setCurrentClient(this.selectedClient);
          this.ref.close(this.selectedClient);
        }
      },
      error: () => (this.isLoading = false),
    });
  }

  filterClients(event: { query: string }) {
    const query = event.query.toLowerCase();
    this.filteredClients = this.allClients.filter(client =>
      client.name.toLowerCase().includes(query)
    );
  }

  onSelect() {
    const currentClient = this.clientContext.getCurrentClient();
    if (
      this.selectedClient &&
      currentClient &&
      currentClient.tenantGuid !== this.selectedClient.tenantGuid
    ) {
      this.confirmDialog.confirm();
    } else if (this.selectedClient) {
      this.commonService.clearClientSelection();
      this.ref.close(this.selectedClient);
    }
  }

  onConfirm() {
    if (this.selectedClient) {
      const tenantGuid = this.selectedClient.tenantGuid;
      this.commonService.clearClientSelection();
      // Flush stale server-side role/feature cache for the target client before
      // navigating so the new client's access is reloaded fresh.
      this.userAccessService.resetUserRoleFeatures(tenantGuid).subscribe({
        next: () => this.finishSwitch(),
        error: () => this.finishSwitch(),
      });
    }
  }

  private finishSwitch() {
    this.ref.close(this.selectedClient);
    this.router.navigate(['/dashboard'], { queryParams: { reset: 1 } });
  }

  onCancel() {
    this.ref.close();
  }
}
